import React, { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import ReactGA from "react-ga4";

import { RoutesRenderer, TAppRoute } from "../common/helpers/routesMapper";
import Seo, { TSeoProps } from "../common/helpers/seo";

interface IProps {
    routes: TAppRoute[],
    isAdmin?: boolean,
    onRouteChange?: () => void,
    seo?: TSeoProps,
}

// The router owns the history object from version 6 on, so what used to be a
// history.listen call at module level is a component inside the router now. listen
// never fired for the first page - that pageview is sent from the client entry - so
// the first run is skipped here too, otherwise it would be counted twice.
/**
 * The head the matched route asked for.
 *
 * Worked out by the server, which is the only side that can: a route describes its head
 * from a store its loadData has already filled, and the browser's store holds none of
 * that on the first render. So the server writes the answer into the document and the
 * browser takes it from there - the same arrangement as the language and the theme.
 *
 * Rendered whether or not there is a head to render, so that the tree the browser
 * hydrates has the same shape as the one the server sent.
 */
const RouteSeo = ({ seo }: Pick<IProps, "seo">) => (seo ? <Seo {...seo} /> : null);

const RouteChangeTracker = ({ isAdmin, onRouteChange }: Omit<IProps, "routes">): null => {
    const location = useLocation();
    const action = useNavigationType();
    const isFirstRun = useRef(true);
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        if (window.ga && window.ga.create && !isAdmin) {
            ReactGA.send({ hitType: "pageview", page: location.pathname });
        }
        window.historyList.push({ location, action });
        onRouteChange?.();
    }, [location]);
    return null;
};

// The server and the browser must render the same tree, not merely an equivalent
// one: React's useId derives its value from a component's position among its
// siblings. Anything added here is added to both sides at once, which is the point.
export const AppRoutes = ({ routes, isAdmin, onRouteChange, seo }: IProps) => (
    <>
        <RouteChangeTracker isAdmin={isAdmin} onRouteChange={onRouteChange} />
        <RouteSeo seo={seo} />
        <div><RoutesRenderer routes={routes} /></div>
    </>
);

export default AppRoutes;
